import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthAPI } from "../../api/authAPI";
import { PROD_BD_API_URL } from "../../api/axiosInstance";
import { EMAIL_USER_TYPE } from "../../utils/constants";
import { getPostAuthDestination } from "../../utils/onboardingRouting";
import { useAuth } from "../../context/AuthContext";
import AuthLayout from "../../components/AuthLayout";
import { Box, Button, Typography } from "@mui/material";
import { triggerGa } from "../../utils/gaUtils";

const RESEND_COUNTDOWN_SECONDS = 30;
const OTP_LENGTH = 6;
const GOOGLE_ICON_URL =
  "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg";

const SolidArrowIcon = () => (
  <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
    <path
      d="M2.5 10H15.5M10.5 4.5L16 10L10.5 15.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GoogleArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="M2.5 10H15.5M10.5 4.5L16 10L10.5 15.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SpinnerWithCount = ({ count }) => (
  <Box
    sx={{
      position: "relative",
      width: 32,
      height: 32,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
      <circle
        cx="16"
        cy="16"
        r="13"
        fill="none"
        stroke="#9b9b9b"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="54"
        strokeDashoffset="36"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 16 16"
          to="360 16 16"
          dur="1s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
    {count > 0 && (
      <Box
        component="span"
        sx={{
          position: "absolute",
          fontSize: count >= 10 ? "11px" : "13px",
          fontWeight: 700,
          color: "#5a5a5a",
          lineHeight: 1,
          userSelect: "none",
          letterSpacing: count >= 10 ? "-0.5px" : "0",
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

export default function OTPVerify() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { setEmailUserAuthToken, setAdminAuthToken } = useAuth();

  const email = useMemo(
    () => location.state?.email?.trim().toLowerCase() || "",
    [location.state?.email],
  );

  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendCountdown, setResendCountdown] = useState(
    RESEND_COUNTDOWN_SECONDS,
  );
  const [resendLoading, setResendLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showResentState, setShowResentState] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    triggerGa({
      category: "Login",
      action: "page_view",
      label: "otp_page",
      page: "login",
      screenName: "otp",
    });
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setResendCountdown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCountdown]);

  const handleResendOtp = async () => {
    if (resendCountdown > 0 || resendLoading || !email) {
      return;
    }
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "resend_otp",
      page: "login",
      screenName: "otp",
    });

    setOtpError("");
    setResendLoading(true);
    try {
      await AuthAPI.sendLoginOTP({ email });
      setShowResentState(true);
      setResendCountdown(RESEND_COUNTDOWN_SECONDS);
      window.setTimeout(() => setShowResentState(false), 3000);
    } catch (apiError) {
      setOtpError(apiError?.response?.data?.message || "Failed to resend OTP.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (otp.length !== OTP_LENGTH) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "otp_submit",
      page: "login",
      screenName: "otp",
    });

    setOtpError("");
    setSubmitLoading(true);
    try {
      const response = await AuthAPI.verifyLoginOTP({
        email,
        otp,
      });

      if (response?.data?.action === "register") {
        triggerGa({
          category: "Login",
          action: "verification_success",
          label: "otp",
          page: "login",
          screenName: "otp",
          eventValue: 1,
          label3: "login_method=email",
        });
        navigate(`/register?email=${encodeURIComponent(email)}`, {
          replace: true,
        });
        return;
      }

      const token = response?.data?.token;
      const userType = response?.data?.user_type || EMAIL_USER_TYPE;
      if (!token) {
        setOtpError("Something went wrong.");
        return;
      }

      await queryClient.cancelQueries({ queryKey: ["currentUser"] });
      queryClient.removeQueries({ queryKey: ["currentUser"] });

      [localStorage, sessionStorage].forEach((storage) => {
        storage.removeItem("Token");
        storage.removeItem("authToken");
        storage.removeItem("user_type");
      });
      localStorage.setItem("authToken", token);
      localStorage.setItem("user_type", userType);
      setEmailUserAuthToken(token);
      setAdminAuthToken(null);
      const meResponse = await AuthAPI.getCurrentUser();
      const destination = getPostAuthDestination(meResponse?.data);
      if (destination === "/register") {
        triggerGa({
          category: "Login",
          action: "verification_success",
          label: "otp",
          page: "login",
          screenName: "otp",
          eventValue: 1,
          label3: "login_method=email",
        });
        navigate(`/register?email=${encodeURIComponent(email)}`, {
          replace: true,
        });
        return;
      }
      triggerGa({
        category: "Login",
        action: "verification_success",
        label: "otp",
        page: "login",
        screenName: "otp",
        eventValue: 1,
        label3: "login_method=email",
      });
      navigate(destination, { replace: true });
    } catch (apiError) {
      triggerGa({
        category: "Error",
        action: "invalid_otp",
        label: "otp",
        page: "login",
        screenName: "otp",
        label3: "failure_reason=invalid_or_expired",
      });
      setOtpError(
        apiError?.response?.data?.message || "OTP verification failed.",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${PROD_BD_API_URL}oauth-login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const resendIsDisabled =
    resendCountdown > 0 || resendLoading || showResentState;

  return (
    <AuthLayout>
      <Box
        sx={{
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          background: "#fbfbfb",
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Typography
          sx={{
            m: "0 0 16px",
            fontSize: 12,
            lineHeight: "20px",
            fontWeight: 600,
            letterSpacing: "0.2px",
            textTransform: "uppercase",
            color: "#0d0d0d",
          }}
        >
          Login/Signup
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              m: 0,
              fontSize: 12,
              lineHeight: "18px",
              letterSpacing: "0.2px",
              fontWeight: 400,
              color: "#8a8a8a",
            }}
          >
            OTP has been sent to{" "}
            <Box component="span" sx={{ color: "#000000", fontWeight: 500 }}>
              {email}
            </Box>
          </Typography>
          <Button
            type="button"
            onClick={() => navigate("/login", { state: { email } })}
            variant="text"
            sx={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              background: "transparent",
              border: "none",
              p: 0,
              mt: 1,
              color: "#ee4d37",
              textDecorationLine: "underline",
              textUnderlineOffset: 2,
              fontSize: 12,
              lineHeight: "18px",
              letterSpacing: "0.2px",
              fontWeight: 500,
              textTransform: "none",
            }}
          >
            Change email
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <Typography
            component="label"
            htmlFor="otp"
            sx={{
              fontSize: 12,
              lineHeight: "18px",
              letterSpacing: "0.2px",
              fontWeight: 500,
              color: "#000000",
            }}
          >
            OTP
          </Typography>
          <Box
            component="input"
            id="otp"
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={(event) => {
              const nextValue = event.target.value
                .replace(/\D/g, "")
                .slice(0, OTP_LENGTH);
              setOtp(nextValue);
              if (otpError) {
                setOtpError("");
              }
            }}
            placeholder="Enter OTP"
            sx={{
              width: "100%",
              height: 40,
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              p: "8px 12px",
              boxSizing: "border-box",
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: "20px",
              letterSpacing: "0.2px",
              fontWeight: 400,
              color: "#0d0d0d",
            }}
          />
        </Box>

        {otpError ? (
          <Typography
            sx={{
              color: "#ee4d37",
              fontSize: 12,
              lineHeight: "18px",
              letterSpacing: "0.2px",
              fontWeight: 400,
              mt: "-8px",
              mb: 1,
            }}
          >
            {otpError}
          </Typography>
        ) : null}

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Button
            type="button"
            onClick={handleResendOtp}
            disabled={resendIsDisabled}
            sx={{
              width: "100%",
              height: 48,
              border: `1px solid ${resendIsDisabled ? "#808080" : "#ee4d37"}`,
              borderRadius: "8px",
              background: resendIsDisabled ? "#e5e5e5" : "#ffffff",
              color: resendIsDisabled ? "#8a8a8a" : "#ee4d37",
              fontSize: 16,
              lineHeight: "20px",
              fontWeight: 600,
              letterSpacing: "0.2px",
              cursor: resendIsDisabled ? "not-allowed" : "pointer",
              textTransform: "none",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {resendCountdown > 0 || resendLoading ? (
                <SpinnerWithCount count={resendLoading ? 0 : resendCountdown} />
              ) : null}
              <span>{showResentState ? "OTP resent" : "Resend OTP"}</span>
            </Box>
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitLoading}
            sx={{
              width: "100%",
              height: 48,
              border: "1px solid #ee4d37",
              borderRadius: "8px",
              background: "#ee4d37",
              color: "#ffffff",
              fontSize: 16,
              lineHeight: "20px",
              fontWeight: 600,
              cursor: submitLoading ? "not-allowed" : "pointer",
              opacity: submitLoading ? 0.7 : 1,
              textTransform: "none",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                letterSpacing: "0.2px",
              }}
            >
              <span>Submit</span>
              <Box
                sx={{
                  width: 20,
                  minWidth: 20,
                  height: 20,
                  textAlign: "center",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SolidArrowIcon />
              </Box>
            </Box>
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          justifyContent: "center",
          my: 2,
        }}
      >
        <Box sx={{ width: 128, borderTop: "1px solid #e3e3e3" }} />
        <Typography
          sx={{
            m: 0,
            fontSize: 16,
            lineHeight: "24px",
            textTransform: "uppercase",
            color: "#8a8a8a",
            fontWeight: 500,
          }}
        >
          OR
        </Typography>
        <Box sx={{ width: 128, borderTop: "1px solid #e3e3e3" }} />
      </Box>

      <Button
        type="button"
        onClick={handleGoogleLogin}
        sx={{
          width: "100%",
          height: 48,
          border: "none",
          borderRadius: "8px",
          background: "#efefef",
          color: "#5a5a5a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "8px 16px",
          textTransform: "none",
          "&:hover": { background: "#efefef" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="img"
            sx={{ width: 24, height: 24 }}
            src={GOOGLE_ICON_URL}
            alt="Google"
          />
          <Typography
            sx={{ fontSize: 16, lineHeight: "20px", fontWeight: 500 }}
          >
            Login with Google
          </Typography>
        </Box>
        <Box
          sx={{
            width: 32,
            minWidth: 32,
            p: "6px",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#5a5a5a",
          }}
        >
          <GoogleArrowIcon />
        </Box>
      </Button>
    </AuthLayout>
  );
}
